import React from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { isEmpty, isNil, negate, uniq } from 'lodash/fp'
import { AlertTriangleIcon, ChevronLeftIcon, XIcon } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	ModalBody,
	ModalClose,
	ModalFooter,
} from '@/shared/components/ui/modal'
import { zodResolver } from '@/shared/lib/validator/zod'

import { FormProvider, RHFSelect } from '../HookForm'
import { useImportFromFile } from './ImportFromFileProvider'

const mappingSchema = z.object({
	file_header_mapping: z
		.record(z.string(), z.string())
		.refine((val) => Object.values(val).includes('sku'))
		.refine(
			(val) =>
				uniq(Object.values(val).filter(negate(isNil))).length ===
				Object.values(val).filter(negate(isNil)).length
		),
})

type ImportFileMappingFormData = z.infer<typeof mappingSchema>

const ImportFromFileMapping = () => {
	const { t } = useTranslation()

	const context = useImportFromFile()
	const mapping = context.headers.reduce(
		(acc, source) => {
			acc[source] =
				context.schemaOptions.find((option) => option.label === source)
					?.value || undefined

			return acc
		},
		{} as Record<string, string | undefined>
	)
	const methods = useForm<ImportFileMappingFormData>({
		resolver: zodResolver(mappingSchema),
		defaultValues: {
			file_header_mapping: mapping as Record<string, string>,
		},
	})
	const handleSubmit = methods.handleSubmit(
		(data: ImportFileMappingFormData) => {
			context.submit(
				{
					...context.draft,
					...data,
				},
				context
			)
		}
	)
	const fileHeaderMapping = useWatch({
		control: methods.control,
		name: 'file_header_mapping',
	})

	const fields = Object.entries(fileHeaderMapping)
	const parentRef = React.useRef(null)

	// The virtualizer
	const rowVirtualizer = useVirtualizer({
		count: fields.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 57,
		overscan: 5,
	})
	const virtualRows = rowVirtualizer.getVirtualItems()
	const totalSize = rowVirtualizer.getTotalSize()
	const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0
	const paddingBottom =
		virtualRows.length > 0
			? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
			: 0

	return (
		<>
			<ModalBody className='relative flex flex-col overflow-hidden px-0'>
				<FormProvider
					methods={methods}
					onSubmit={handleSubmit}
					className='flex flex-col gap-1 px-4'
				>
					<p className='text-sm text-muted-foreground'>
						{t('import.mapping.description', {
							defaultValue:
								'Select the column that will be used to map the data.',
						})}
					</p>
					{!isEmpty(methods.formState.errors?.file_header_mapping?.message) && (
						<Alert variant='destructive'>
							<AlertTriangleIcon />
							<AlertDescription>
								{t(
									methods.formState.errors?.file_header_mapping
										?.message as unknown as string,
									{
										defaultValue:
											'Please select a target column for each source column',
									}
								)}
							</AlertDescription>
						</Alert>
					)}
					<div className='mt-5 grid grid-cols-[min(50%,--spacing(80))_auto] border-b pb-2'>
						<span className='text-sm font-semibold text-muted-foreground'>
							{t('import.mapping.column_in_file', {
								defaultValue: 'Column in file',
							})}
						</span>
						<span className='text-sm font-semibold text-muted-foreground'>
							{t('import.mapping.column_in_system', {
								defaultValue: 'Column in system',
							})}
						</span>
					</div>

					<div
						ref={parentRef}
						className='-mx-4 flex h-100 overflow-y-auto px-4'
					>
						<div
							className='relative w-full divide-y'
							style={{
								height: `${totalSize}px`,
							}}
						>
							{paddingTop > 0 && <div style={{ height: `${paddingTop}px` }} />}
							{virtualRows.map((virtualItem) => {
								const [source] = fields[virtualItem.index]

								return (
									<div
										key={source}
										ref={rowVirtualizer.measureElement}
										className='grid grid-cols-1 text-base/6 sm:grid-cols-[min(50%,--spacing(80))_auto] sm:text-sm/6'
									>
										<div className='col-start-1 flex items-start gap-2 border-t pt-3 first:border-none sm:border-t sm:py-3'>
											<Button
												size='icon-sm'
												variant='ghost'
												onClick={() => {
													const currentMapping = {
														...methods.getValues('file_header_mapping'),
													}

													delete currentMapping[source]
													methods.setValue(
														'file_header_mapping',
														currentMapping
													)
												}}
											>
												<XIcon />
											</Button>
											<div className='py-1 text-sm font-medium'>
												{t(source, { ns: 'fields', defaultValue: source })}
											</div>
										</div>
										<div className='pt-1 pb-3 sm:border-t sm:py-3 sm:nth-2:border-none'>
											<RHFSelect
												className='w-full'
												name={`file_header_mapping.${source}`}
												options={context.schemaOptions || []}
												placeholder={t(
													'import.mapping.select_target.placeholder',
													{ defaultValue: 'Select target' }
												)}
											/>
										</div>
									</div>
								)
							})}
							{paddingBottom > 0 && (
								<div style={{ height: `${paddingBottom}px` }} />
							)}
						</div>
					</div>
				</FormProvider>
			</ModalBody>
			<ModalFooter className='justify-between gap-2'>
				<ModalClose asChild className='mr-auto'>
					<Button>{t('general_cancel')}</Button>
				</ModalClose>
				<Button onClick={() => context.setStep('form')}>
					<ChevronLeftIcon />
					{t('general_back')}
				</Button>
				<Button variant='primary' onClick={handleSubmit}>
					{t('general_apply')}
				</Button>
			</ModalFooter>
		</>
	)
}

export { ImportFromFileMapping, type ImportFileMappingFormData }
